/**
 * Data Fetch Request Page
 *
 * Main page component for data fetch request management
 * Orchestrates table and drawer components
 * Handles data fetching and permissions
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DrawerProvider } from 'equal-ds-ui';
import { DataFetchTable } from './DataFetchTable';
import { DataFetchDrawer } from './DataFetchDrawer';
import { dataFetchAPI } from '../../../services/data-fetch-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { DEFAULT_PAGE_SIZE, DEFAULT_CURRENT_PAGE } from '../../../constants/pagination.constants';
import type {
  DataFetchRequestPageProps,
  DataFetchRequest,
  FipAccountGroup,
  MenuPermission,
} from '../../../types/data-fetch-request';

export const DataFetchRequestPage: React.FC<DataFetchRequestPageProps> = ({
  routeParams,
  queryParams,
  currentRoute,
}) => {
  console.log('[DataFetchRequestPage] Mounted with props:', {
    routeParams,
    queryParams,
    currentRoute,
  });

  // State
  const [dataFetchRequests, setDataFetchRequests] = useState<DataFetchRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccountDetailsPermission, setHasAccountDetailsPermission] = useState(true);
  const [canDownloadCSV, setCanDownloadCSV] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DataFetchRequest | null>(null);

  // Error handling
  const { handleError } = useErrorHandler();

  // Check user permissions on mount
  useEffect(() => {
    checkPermissions();
  }, []);

  // Fetch data on mount and when pagination changes
  useEffect(() => {
    fetchDataFetchRequests();
  }, [currentPage, pageSize]);

  /**
   * Check user permissions for account details feature
   */
  const checkPermissions = useCallback(() => {
    try {
      const userInfoEncoded = sessionStorage.getItem('loggedUserInfo');
      if (!userInfoEncoded) {
        console.warn('[DataFetchRequestPage] No user info found in sessionStorage');
        return;
      }

      const userInfo = JSON.parse(atob(userInfoEncoded));
      const menuList: MenuPermission[] = userInfo.menuList || [];

      // Find DATA-FETCH-REQUEST menu
      const dataFetchMenu = menuList.find(
        (menu) => menu.code === 'DATA-FETCH-REQUEST'
      );

      if (!dataFetchMenu) {
        console.warn('[DataFetchRequestPage] DATA-FETCH-REQUEST menu not found');
        setHasAccountDetailsPermission(false);
        return;
      }

      // Check for Account-Details feature
      const hasPermission = dataFetchMenu.features.some(
        (feature) => feature.code.trim() === 'Account-Details'
      );
      setHasAccountDetailsPermission(hasPermission);

      // Check for Download CSV feature
      const hasDownloadCSV = dataFetchMenu.features.some(
        (feature) => feature.code.trim() === 'DATAFETCHREQUEST-DOWNLOADCSV'
      );
      setCanDownloadCSV(hasDownloadCSV);

      console.log('[DataFetchRequestPage] Permissions:', { hasPermission, hasDownloadCSV });
    } catch (error) {
      console.error('[DataFetchRequestPage] Failed to check permissions:', error);
      setHasAccountDetailsPermission(false);
      setCanDownloadCSV(false);
    }
  }, []);

  /**
   * Fetch data fetch requests from API
   */
  const fetchDataFetchRequests = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await dataFetchAPI.getAllDataFetchRequests({
        limit: pageSize,
        pageno: currentPage,
      });

      const rows = response.data.rows || [];
      const count = response.data.count || 0;

      // Transform data to ensure customer_vua is available
      const transformedRows = rows.map((row) => ({
        ...row,
        customer_vua:
          row.customer_consents_request?.customer_vua || row.customer_vua || '',
      }));

      setDataFetchRequests(transformedRows);
      setTotalCount(count);

      console.log('[DataFetchRequestPage] Fetched data:', {
        rows: transformedRows.length,
        total: count,
        page: currentPage,
      });
    } catch (error) {
      console.error('[DataFetchRequestPage] Failed to fetch data:', error);
      handleError(error, { component: 'DataFetchRequestPage', action: 'fetchData' });
      setDataFetchRequests([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, handleError]);

  /**
   * Handle view account details action
   */
  const handleViewAccountDetails = useCallback((row: DataFetchRequest) => {
    console.log('[DataFetchRequestPage] View account details for:', row.consent_id);
    setSelectedRequest(row);
    setDrawerOpen(true);
  }, []);

  /**
   * Generate FIP account groups for drawer
   */
  const fipAccountGroups = useMemo<FipAccountGroup[]>(() => {
    if (!selectedRequest || !selectedRequest.accounts) {
      return [];
    }

    // Group accounts by FIP ID
    const groupsMap = new Map<string, FipAccountGroup>();

    selectedRequest.accounts.forEach((account) => {
      const fipId = account.fip_id;
      if (!groupsMap.has(fipId)) {
        groupsMap.set(fipId, {
          fip_id: fipId,
          accounts: [],
        });
      }
      groupsMap.get(fipId)!.accounts.push(account);
    });

    return Array.from(groupsMap.values());
  }, [selectedRequest]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  }, []);

  return (
    <DrawerProvider side="right" open={drawerOpen} onOpenChange={setDrawerOpen}>
      <div className="h-full flex flex-col p-4">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
            Data Fetch Request List
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            View and manage data fetch requests with customer account details.
          </p>
        </div>

        {/* Table */}
        <DataFetchTable
          data={dataFetchRequests}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewAccountDetails={handleViewAccountDetails}
          isLoading={isLoading}
          hasAccountDetailsPermission={hasAccountDetailsPermission}
          canDownloadCSV={canDownloadCSV}
        />
      </div>

      {/* Drawer - Moved outside content div to be a sibling */}
      <DataFetchDrawer
        selectedRequest={selectedRequest}
        fipAccountGroups={fipAccountGroups}
      />
    </DrawerProvider>
  );
};
